import React from "react";
import { Link } from "react-router-dom";
import "./NeoLandingPage.css";

const flowSections = [
  {
    number: "01",
    eyebrow: "Live operational picture",
    title: "Can you see what needs attention?",
    copy:
      "Neo gives leaders one view of today's execution health: what is complete, what is late, who is active, and which duties need a response now.",
    bullets: ["Live execution health", "Multi-site duty progress", "Exceptions surfaced early", "One truth for operations"],
    visual: "overview",
  },
  {
    number: "02",
    eyebrow: "Standards that can run",
    title: "Can every site execute the same standard?",
    copy:
      "Build standards once, publish them across sites, and give every section a clear operating rhythm with duties, alarms, proof and response coverage.",
    bullets: ["Opening and closing standards", "Section-based duties", "Reusable operational playbooks", "Publish controlled updates"],
    visual: "standards",
  },
  {
    number: "03",
    eyebrow: "Execution designed upfront",
    title: "How do you know the work actually happened?",
    copy:
      "Every duty can carry an evidence requirement, due time, reminder and escalation path so the response is designed before execution starts.",
    bullets: ["Photo, checklist, numeric and text proof", "Due times and reminder windows", "Escalation routing by role", "Draft and publish workflow"],
    visual: "edit",
  },
  {
    number: "04",
    eyebrow: "Response intelligence",
    title: "What happens when execution drifts?",
    copy:
      "Neo does not stop at showing that something went wrong. It follows the response path from reminder, to escalation, to proof, to resolution.",
    bullets: ["Automatic reminders", "Manager escalation", "Proof received in context", "Full response history"],
    visual: "response",
  },
];

const navLinks = [
  ["#why", "Why Neo"],
  ["#action", "In Action"],
  ["#floor", "Staff App"],
  ["#assurance", "Assurance"],
];

function MiniNav() {
  return (
    <header className="neo-page-nav">
      <Link to="/" className="neo-page-brand" aria-label="MatrixOS home">
        <span className="neo-page-mark">M</span>
        <span>
          <strong>MATRIXOS</strong>
          <small>OPERATE WITH CLARITY</small>
        </span>
      </Link>
      <nav>
        {navLinks.map(([href, label]) => (
          <a key={href} href={href}>{label}</a>
        ))}
        <Link to="/explore/morpheus">Morpheus</Link>
        <Link to="/explore/link">Link</Link>
        <Link className="neo-page-demo" to="/book-demo">Book a demo</Link>
      </nav>
    </header>
  );
}

function DeviceFrame({ children, className = "" }) {
  return (
    <div className={`neo-device neo-laptop ${className}`}>
      <div className="neo-device-top"><span /><span /><span /><b>Neo Command Centre</b></div>
      {children}
    </div>
  );
}

function NeoOverviewShowcase({ hero = false }) {
  const sites = [
    ["District Six", "98%", "Stable"],
    ["Parow", "92%", "Attention"],
    ["Bothasig", "100%", "Stable"],
    ["Mowbray", "88%", "Escalated"],
  ];

  return (
    <DeviceFrame className={hero ? "hero-device" : ""}>
      <div className="neo-admin-shell">
        <aside>
          <b>The Venue<br />Cape Town</b>
          {['Overview', 'Staff Dashboard', 'Standards', 'Response Centre', 'Reports'].map((item, i) => (
            <em key={item} className={i === 0 ? 'active' : ''}>{item}</em>
          ))}
        </aside>
        <main>
          <div className="neo-mock-head">
            <div>
              <small>Monday · Live operations</small>
              <span>Overview</span>
            </div>
            <strong>OPERATIONS STABLE</strong>
          </div>

          <div className="neo-focus-grid">
            <div className="attention"><small>Focus now</small><b>4 responses in progress</b><a>Open Response Centre</a></div>
            <div><small>Execution today</small><b>198 of 204 duties complete</b><a>View duties</a></div>
            <div><small>Evidence</small><b>76 photos submitted</b><a>View proof</a></div>
          </div>

          <div className="neo-kpi-row">
            {[
              ['Sites Active','8','+2'],
              ['Staff On Shift','34','live'],
              ['On Time','97.9%','+4.1%'],
              ['Overdue','6','-3'],
              ['Escalated','1','watch'],
            ].map(([l,v,t]) => <div key={l}><small>{l}</small><b>{v}</b><i>{t}</i></div>)}
          </div>

          <div className="neo-ops-grid">
            <div className="neo-chart-card">
              <div className="neo-card-head"><b>Execution pace</b><small>Today vs yesterday</small></div>
              <div className="neo-chart"><span /><span /><span /><span /><span /><span /></div>
            </div>
            <div className="neo-site-card">
              <div className="neo-card-head"><b>Sites</b><small>Duty completion</small></div>
              {sites.map(([site, pct, state]) => (
                <div className="neo-site-row" key={site}>
                  <span>{site}</span><div><i style={{ width: pct }} /></div><em className={state.toLowerCase()}>{state}</em>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </DeviceFrame>
  );
}

function NeoStandardsShowcase() {
  const standards = [
    ["Opening Duties", "42 duties", "18 alarms", "Published"],
    ["Closing Duties", "36 duties", "22 alarms", "Published"],
    ["Cleaning Standards", "19 duties", "8 alarms", "Draft"],
    ["Safety & Compliance", "14 duties", "12 alarms", "Review"],
  ];
  return (
    <div className="neo-show-card standards-card">
      <div className="neo-show-title">
        <div><small>Neo Builder</small><span>Standards Library</span></div>
        <button>+ New Standard</button>
      </div>
      <div className="neo-builder-layout">
        <div className="neo-builder-list">
          {standards.map(([name, duties, alarms, status], i) => (
            <div className={`neo-standard-row ${i === 0 ? 'selected' : ''}`} key={name}>
              <span>{name}</span>
              <small>{duties}</small>
              <small>{alarms}</small>
              <em>{status}</em>
            </div>
          ))}
        </div>
        <div className="neo-builder-panel">
          <b>Opening Duties</b>
          <p>Sections included in this standard</p>
          {['Front of House', 'Bar', 'Manager Checks'].map((item, i) => (
            <div className="neo-section-chip" key={item}><span>{item}</span><em>{[14,18,10][i]} duties</em></div>
          ))}
          <div className="neo-response-coverage">
            <strong>Response coverage</strong>
            <div><span /> <small>18 alarms active · 3 escalation rules missing</small></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NeoEditStandardShowcase() {
  return (
    <div className="neo-show-card edit-card">
      <div className="neo-edit-head">
        <div>
          <small>Standard editor</small>
          <span>Edit Standard / Opening Duties</span>
        </div>
        <div><b>42</b><small>Total duties</small></div>
        <div><b>18</b><small>Active alarms</small></div>
        <button>Publish</button>
      </div>
      <div className="neo-editor-grid">
        <div className="neo-duty-table">
          {[
            ['Lights switched on', 'Checkbox', '07:00', 'Alarm on'],
            ['All TV switched on', 'Checkbox', '07:05', 'Alarm off'],
            ['Bin empty in bar', 'Photo', '07:10', 'Alarm on'],
            ['Opening signage checked', 'Photo', '07:15', 'Alarm on'],
            ['Cash drawer ready', 'Numeric', '07:20', 'Alarm on'],
          ].map(([d,t,time,a]) => (
            <div key={d}>
              <b>{d}</b><span>{t}</span><span>{time}</span><em className={a === 'Alarm on' ? 'on' : ''}>{a}</em>
            </div>
          ))}
        </div>
        <div className="neo-rule-card">
          <small>Response rule</small>
          <h4>Bin empty in bar</h4>
          <label>Evidence required</label>
          <p>Photo proof before 07:10</p>
          <label>If overdue</label>
          <p>Remind staff after 5 min</p>
          <label>If still overdue</label>
          <p>Escalate to manager after 10 min</p>
          <div className="neo-draft-pill">Draft saved · 10:31</div>
        </div>
      </div>
    </div>
  );
}

function NeoResponseCentreShowcase() {
  const steps = [
    ["07:00", "Duty due", "Opening lights must be switched on"],
    ["07:05", "Reminder sent", "Neo reminds assigned staff member"],
    ["07:10", "Manager notified", "Duty is overdue and now visible"],
    ["07:14", "Evidence received", "Photo proof uploaded from floor"],
    ["07:16", "Resolved", "Manager sees completed proof"],
  ];
  return (
    <div className="neo-show-card response-card">
      <div className="neo-response-title-wrap">
        <div><small>Response Centre</small><span>Execution drift detected</span></div>
        <strong>RESOLVED</strong>
      </div>
      <div className="neo-response-workspace">
        <div className="neo-response-timeline">
          {steps.map(([time, title, desc], i) => (
            <div key={title} className={`neo-response-event e${i}`}>
              <i />
              <small>{time}</small>
              <b>{title}</b>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        <div className="neo-evidence-panel">
          <div className="neo-photo-preview">PHOTO EVIDENCE</div>
          <b>Lights switched on</b>
          <p>Submitted by Lerato · District Six · 07:14</p>
          <span>✓ Proof accepted</span>
        </div>
      </div>
    </div>
  );
}

function Visual({ type }) {
  if (type === "overview") return <NeoOverviewShowcase />;
  if (type === "standards") return <NeoStandardsShowcase />;
  if (type === "edit") return <NeoEditStandardShowcase />;
  return <NeoResponseCentreShowcase />;
}

function NeoStaffMobileShowcase() {
  const phones = [
    {
      title: "Today",
      subtitle: "Opening duties",
      body: ['Lights switched on', 'TV switched on', 'Bin empty in bar'],
      type: 'list',
    },
    {
      title: "Evidence",
      subtitle: "Photo required",
      body: [],
      type: 'photo',
    },
    {
      title: "Complete",
      subtitle: "Opening duty resolved",
      body: [],
      type: 'success',
    },
  ];
  return (
    <div className="neo-phone-group">
      {phones.map((phone, i) => (
        <div className="neo-phone" key={phone.title}>
          <div className="neo-phone-notch" />
          <small>Neo Staff</small>
          <b>{phone.title}</b>
          <em>{phone.subtitle}</em>
          <div className={`neo-phone-body p${i}`}>
            {phone.type === 'list' && phone.body.map((x, idx) => (
              <span key={x} className={idx === 2 ? 'due' : ''}><i>{idx === 2 ? '!' : '✓'}</i>{x}</span>
            ))}
            {phone.type === 'photo' && <><div className="photo-box">CAMERA</div><button>Submit evidence</button></>}
            {phone.type === 'success' && <><div className="success-check">✓</div><strong>Great work!</strong><small>Manager can now see proof</small></>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NeoLandingPage() {
  return (
    <main className="neo-landing-page">
      <MiniNav />

      <section className="neo-hero" id="why">
        <div className="neo-hero-copy">
          <span className="neo-pill">Neo · Execution Assurance</span>
          <h1>Make execution visible, provable and response-ready.</h1>
          <p>Neo turns standards, duties, proof, reminders and escalations into one live command layer for real-world operations.</p>
          <div className="neo-hero-actions">
            <Link to="/book-demo">Book a demo</Link>
            <a href="#action">See Neo in action</a>
          </div>
          <div className="neo-hero-principles">
            {['Design the execution', 'Track in real time', 'Capture proof', 'Respond before drift spreads'].map((x) => <span key={x}>{x}</span>)}
          </div>
        </div>
        <div className="neo-hero-visual">
          <div className="neo-floating-badge b1"><b>34</b><span>staff on shift</span></div>
          <div className="neo-floating-badge b2"><b>97.9%</b><span>on time today</span></div>
          <NeoOverviewShowcase hero />
        </div>
      </section>

      <section className="neo-story" id="action">
        <div className="neo-section-heading">
          <small>Neo in action</small>
          <h2>From standard to assurance.</h2>
          <p>Each scroll answers the question an operator is already asking before the day goes off track.</p>
        </div>
        {flowSections.map((section) => (
          <article className="neo-story-row" key={section.number}>
            <div className="neo-story-copy">
              <span className="neo-number">{section.number}</span>
              <small>{section.eyebrow}</small>
              <h3>{section.title}</h3>
              <p>{section.copy}</p>
            </div>
            <div className="neo-story-visual"><Visual type={section.visual} /></div>
            <ul className="neo-bullet-list">
              {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
            </ul>
          </article>
        ))}
      </section>

      <section className="neo-proof-section" id="floor">
        <div>
          <span className="neo-pill">Staff experience</span>
          <h2>Execution happens on the floor, not in admin.</h2>
          <p>Staff see what is due, complete duties simply, and submit proof without needing the manager to chase.</p>
        </div>
        <NeoStaffMobileShowcase />
      </section>

      <section className="neo-final-cta" id="assurance">
        <div className="neo-cta-icon">✓</div>
        <div>
          <h2>Not just visibility. Execution Assurance.</h2>
          <p>Neo ensures the right work gets done, at the right time, with the right response when things go off track.</p>
        </div>
        <div className="neo-final-actions">
          <Link to="/book-demo">Book a demo</Link>
          <Link to="/explore/morpheus">Explore Morpheus →</Link>
        </div>
      </section>
    </main>
  );
}
